import { useEffect, useState } from 'react';
import { zonasApi, usuariosApi, referenciaApi, municipiosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { ProvinciaSelector } from '../components/ProvinciaSelector';
import { PaisesSelector } from '../components/PaisesSelector';
import { Plus, Pencil, Trash2, UserCheck, UserX, Map, Split, Search } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = () => ({
  nombre: '', descripcion: '', provincias: [], titular_user_id: '',
  municipios_por_provincia: {},
  paises: [],
});

const Zonas = () => {
  const { isSuperAdmin } = useAuth();
  const [zonas, setZonas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [paisesCatalogo, setPaisesCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState('');
  const [coberturaDialog, setCoberturaDialog] = useState(null); // zona activa
  const [cubridor, setCubridor] = useState('');
  const [coberturaNotas, setCoberturaNotas] = useState('');

  // Sub-dialog municipios por provincia (provincia compartida)
  const [muniDialog, setMuniDialog] = useState(null); // { provincia }
  const [muniList, setMuniList] = useState([]);
  const [muniLoading, setMuniLoading] = useState(false);
  const [muniSearch, setMuniSearch] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [zs, us, pr, ps] = await Promise.all([
        zonasApi.list(),
        usuariosApi.list({ estado: 'activo' }).catch(() => []),
        referenciaApi.provincias().catch(() => []),
        referenciaApi.paises(true).catch(() => []),
      ]);
      setZonas(zs); setUsuarios(us); setProvincias(pr); setPaisesCatalogo(ps);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const abrirNueva = () => {
    setEditing(null); setForm(emptyForm()); setErr(''); setDialogOpen(true);
  };
  const abrirEditar = (z) => {
    setEditing(z);
    setForm({
      nombre: z.nombre, descripcion: z.descripcion || '',
      provincias: z.provincias || [], titular_user_id: z.titular_user_id || '',
      municipios_por_provincia: { ...(z.municipios_por_provincia || {}) },
      paises: z.paises || [],
    });
    setErr(''); setDialogOpen(true);
  };
  const guardar = async (ev) => {
    ev.preventDefault(); setErr('');
    if (!form.nombre.trim()) { setErr('Nombre obligatorio'); return; }
    try {
      // Sanear mpp: eliminar entradas cuya provincia ya no está seleccionada
      const provsSet = new Set(form.provincias);
      const mpp = {};
      Object.entries(form.municipios_por_provincia || {}).forEach(([k, v]) => {
        if (provsSet.has(k) && Array.isArray(v) && v.length > 0) mpp[k] = v;
      });
      const payload = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        provincias: form.provincias,
        paises: form.paises || [],
        municipios_por_provincia: mpp,
        titular_user_id: form.titular_user_id || null,
      };
      if (editing) await zonasApi.update(editing.id, payload);
      else await zonasApi.create(payload);
      setDialogOpen(false);
      toast.success(editing ? 'Zona actualizada' : 'Zona creada');
      cargar();
    } catch (e) { setErr(e.message); }
  };

  // === Gestión provincia compartida ===
  const abrirMunicipiosDialog = async (provincia) => {
    setMuniDialog({ provincia });
    setMuniSearch('');
    setMuniLoading(true);
    try {
      const lista = await municipiosApi.listByProvincia(provincia);
      setMuniList(lista);
    } catch (e) {
      toast.error(e.message);
      setMuniList([]);
    } finally {
      setMuniLoading(false);
    }
  };
  const municipiosSeleccionados = muniDialog
    ? (form.municipios_por_provincia?.[muniDialog.provincia] || [])
    : [];
  const esParcial = muniDialog && municipiosSeleccionados.length > 0 && municipiosSeleccionados.length < muniList.length;
  const toggleMuni = (codigo) => {
    const actuales = new Set(form.municipios_por_provincia?.[muniDialog.provincia] || []);
    if (actuales.has(codigo)) actuales.delete(codigo); else actuales.add(codigo);
    setForm({
      ...form,
      municipios_por_provincia: {
        ...(form.municipios_por_provincia || {}),
        [muniDialog.provincia]: [...actuales],
      },
    });
  };
  const seleccionarTodosMuni = () => {
    setForm({
      ...form,
      municipios_por_provincia: {
        ...(form.municipios_por_provincia || {}),
        [muniDialog.provincia]: [],  // vacío = cobertura total
      },
    });
  };
  const quitarTodosMuni = () => {
    setForm({
      ...form,
      municipios_por_provincia: {
        ...(form.municipios_por_provincia || {}),
        [muniDialog.provincia]: ['__NONE__'], // marcador "ninguno"; se limpia al guardar
      },
    });
  };
  const borrar = async (z) => {
    if (!window.confirm(`¿Eliminar la zona "${z.nombre}"? Las provincias volverán a la gestión manual de los usuarios afectados.`)) return;
    try {
      await zonasApi.delete(z.id);
      toast.success('Zona eliminada');
      cargar();
    } catch (e) { toast.error(e.message); }
  };
  const abrirCobertura = (z) => {
    setCoberturaDialog(z);
    setCubridor(''); setCoberturaNotas('');
  };
  const confirmarCobertura = async () => {
    if (!cubridor) { toast.error('Selecciona a quién cubre la zona'); return; }
    try {
      await zonasApi.cubrir(coberturaDialog.id, { cubierto_por_user_id: cubridor, notas: coberturaNotas || null });
      setCoberturaDialog(null);
      toast.success('Cobertura activada — las provincias pasan temporalmente al sustituto');
      cargar();
    } catch (e) { toast.error(e.message); }
  };
  const devolver = async (z) => {
    if (!window.confirm(`¿Devolver la cobertura de "${z.nombre}" a ${z.titular_nombre || 'el titular'}?`)) return;
    try {
      await zonasApi.devolver(z.id);
      toast.success('Zona devuelta al titular');
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  if (!isSuperAdmin) return <Card className="p-6 text-ink-500">Acceso restringido.</Card>;

  return (
    <div data-testid="zonas-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold text-ink-900">Zonas</h1>
          <p className="text-ink-500 mt-1">
            Agrupa provincias, asigna un titular comercial y gestiona coberturas temporales (vacaciones, bajas).
          </p>
        </div>
        <Button className="bg-brand-500 hover:bg-brand-600 text-white shadow-sm" onClick={abrirNueva} data-testid="btn-nueva-zona">
          <Plus className="w-4 h-4 mr-1.5" /> Nueva zona
        </Button>
      </div>

      <Card className="border-ink-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200 text-ink-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Zona</th>
                <th className="px-4 py-3 text-left font-semibold">Provincias</th>
                <th className="px-4 py-3 text-left font-semibold">Titular</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100" data-testid="zonas-tbody">
              {loading && (<tr><td colSpan={5} className="text-center py-12 text-ink-400">Cargando…</td></tr>)}
              {!loading && zonas.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-ink-400">Aún no hay zonas. Crea la primera con el botón de arriba.</td></tr>
              )}
              {!loading && zonas.map((z) => {
                const cubierto = Boolean(z.cubierto_por_user_id);
                return (
                  <tr key={z.id} data-testid={`row-zona-${z.id}`} className="hover:bg-ink-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                          <Map className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-semibold text-ink-900 leading-tight">{z.nombre}</p>
                          {z.descripcion && <p className="text-xs text-ink-500">{z.descripcion}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {z.provincias.length === 0 && (z.paises?.length || 0) === 0 && <span className="text-ink-300 text-xs">—</span>}
                        {z.provincias.slice(0, 6).map((p) => {
                          const mpp = z.municipios_por_provincia?.[p];
                          const parcial = Array.isArray(mpp) && mpp.length > 0;
                          return (
                            <span
                              key={p}
                              className={`text-[11px] px-2 py-0.5 rounded ${parcial ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-ink-100 text-ink-700'}`}
                              title={parcial ? `Compartida · ${mpp.length} municipios` : 'Provincia completa'}
                            >
                              {p}{parcial ? ` (${mpp.length})` : ''}
                            </span>
                          );
                        })}
                        {z.provincias.length > 6 && <span className="text-[11px] text-ink-500">+{z.provincias.length - 6}</span>}
                        {(z.paises || []).map((c) => (
                          <span key={c} className="text-[11px] bg-sky-50 border border-sky-200 text-sky-700 px-2 py-0.5 rounded" title="País entero">
                            🌍 {c}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ink-700">
                      {z.titular_nombre || <span className="text-ink-300">— sin titular —</span>}
                    </td>
                    <td className="px-4 py-3">
                      {cubierto ? (
                        <Badge className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px]">
                          Cubierto por {z.cubierto_por_nombre || '?'}
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px]">Activa (titular)</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {cubierto ? (
                          <button
                            onClick={() => devolver(z)}
                            className="px-2 py-1 rounded hover:bg-emerald-50 text-emerald-700 text-xs inline-flex items-center gap-1"
                            title="Devolver al titular"
                            data-testid={`btn-devolver-${z.id}`}
                          >
                            <UserCheck className="w-3.5 h-3.5" /> Devolver
                          </button>
                        ) : (
                          <button
                            onClick={() => abrirCobertura(z)}
                            disabled={!z.titular_user_id}
                            className="px-2 py-1 rounded hover:bg-amber-50 text-amber-700 text-xs inline-flex items-center gap-1 disabled:opacity-40"
                            title={z.titular_user_id ? 'Asignar cobertura temporal' : 'Asigna un titular primero'}
                            data-testid={`btn-cubrir-${z.id}`}
                          >
                            <UserX className="w-3.5 h-3.5" /> Cubrir
                          </button>
                        )}
                        <button onClick={() => abrirEditar(z)} className="p-1.5 rounded hover:bg-ink-100 text-ink-600" title="Editar" data-testid={`btn-editar-zona-${z.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => borrar(z)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar" data-testid={`btn-borrar-zona-${z.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog crear/editar zona */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-zona">
          <form onSubmit={guardar}>
            <DialogHeader>
              <DialogTitle>{editing ? 'Editar zona' : 'Nueva zona'}</DialogTitle>
              <DialogDescription>
                Agrupa provincias bajo un nombre y asigna un titular. Al cubrir, las provincias pasan temporalmente al sustituto.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Nombre *</label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Levante, Norte, Centro-Sur…" autoFocus data-testid="zf-nombre" />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Descripción</label>
                <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Notas opcionales" />
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Titular (comercial dueño de la zona)</label>
                <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={form.titular_user_id} onChange={(e) => setForm({ ...form, titular_user_id: e.target.value })} data-testid="zf-titular">
                  <option value="">— Sin titular —</option>
                  {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''} · {u.rol}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">Provincias ({form.provincias.length})</label>
                <ProvinciaSelector
                  provincias={provincias}
                  selected={form.provincias}
                  onChange={(lista) => setForm({ ...form, provincias: lista })}
                />
                {form.provincias.length > 0 && (
                  <div className="mt-2 border border-ink-200 rounded-md bg-ink-50 p-2">
                    <p className="text-[11px] font-semibold text-ink-600 mb-1.5 uppercase tracking-wider">Cobertura por provincia</p>
                    <div className="space-y-1">
                      {form.provincias.map((p) => {
                        const mpp = form.municipios_por_provincia?.[p];
                        const parcial = Array.isArray(mpp) && mpp.length > 0 && mpp[0] !== '__NONE__';
                        return (
                          <div key={p} className="flex items-center justify-between text-sm bg-white border border-ink-200 rounded px-2 py-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-medium text-ink-800 truncate">{p}</span>
                              {parcial ? (
                                <span className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5 rounded" data-testid={`mpp-chip-${p}`}>
                                  Compartida · {mpp.length} municipios
                                </span>
                              ) : (
                                <span className="text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded">
                                  Toda la provincia
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => abrirMunicipiosDialog(p)}
                              className="text-xs text-brand-600 hover:text-brand-700 inline-flex items-center gap-1"
                              data-testid={`btn-compartir-${p}`}
                            >
                              <Split className="w-3.5 h-3.5" /> {parcial ? 'Editar municipios' : 'Compartir por municipios'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-ink-500 mt-1.5 leading-snug">
                      Marca "Compartir por municipios" sólo cuando una provincia deba partirse entre dos zonas (uso típico: 1-2 provincias grandes). Por defecto cada provincia se cubre entera.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-ink-600 mb-1 block">
                  Países enteros ({form.paises.length})
                  <span className="ml-2 text-ink-400">(export managers — cubre todas las empresas del país, útil para ES/PT/NL/UK/GI/GY…)</span>
                </label>
                <PaisesSelector
                  paises={paisesCatalogo}
                  selected={form.paises}
                  onChange={(lista) => setForm({ ...form, paises: lista })}
                />
                {(form.paises.includes('ES') || form.paises.includes('PT')) && (
                  <p className="text-[11px] text-amber-700 mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠ Has marcado {form.paises.includes('ES') && 'España'}{form.paises.includes('ES') && form.paises.includes('PT') && ' y '}{form.paises.includes('PT') && 'Portugal'} como país entero — el titular verá TODAS las empresas de ese país, incluso fuera de las provincias seleccionadas arriba. Úsalo sólo para export managers.
                  </p>
                )}
              </div>
              {err && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="btn-guardar-zona">
                {editing ? 'Guardar' : 'Crear zona'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog cobertura */}
      <Dialog open={Boolean(coberturaDialog)} onOpenChange={(o) => { if (!o) setCoberturaDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-cobertura">
          <DialogHeader>
            <DialogTitle>Cubrir "{coberturaDialog?.nombre}"</DialogTitle>
            <DialogDescription>
              Las provincias de esta zona pasarán temporalmente al sustituto. El titular <span className="font-semibold">{coberturaDialog?.titular_nombre}</span> las recuperará al pulsar "Devolver".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-ink-600 mb-1 block">Cubre esta zona *</label>
              <select className="w-full h-10 rounded-md border border-ink-200 bg-white text-sm px-3" value={cubridor} onChange={(e) => setCubridor(e.target.value)} data-testid="cobertura-cubridor">
                <option value="">— Elegir —</option>
                {usuarios.filter((u) => u.id !== coberturaDialog?.titular_user_id).map((u) => (
                  <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''} · {u.rol}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-600 mb-1 block">Notas (motivo, fechas previstas…)</label>
              <Textarea rows={2} value={coberturaNotas} onChange={(e) => setCoberturaNotas(e.target.value)} placeholder="Vacaciones julio · baja paternidad…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoberturaDialog(null)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={confirmarCobertura} data-testid="btn-confirmar-cobertura">
              <UserX className="w-4 h-4 mr-1.5" /> Activar cobertura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog sub-selector municipios por provincia (provincia compartida) */}
      <Dialog open={Boolean(muniDialog)} onOpenChange={(o) => { if (!o) setMuniDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="dialog-municipios-mpp">
          <DialogHeader>
            <DialogTitle>Municipios de {muniDialog?.provincia} incluidos en esta zona</DialogTitle>
            <DialogDescription>
              Sólo para provincias compartidas entre dos zonas. Si no marcas ninguno, la zona cubre <strong>toda la provincia</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 border-b border-ink-200 py-2 px-1">
            <Search className="w-4 h-4 text-ink-400" />
            <input
              type="text"
              value={muniSearch}
              onChange={(e) => setMuniSearch(e.target.value)}
              placeholder="Buscar municipio…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-400"
              data-testid="muni-search"
            />
            <button type="button" className="text-xs text-brand-600 hover:text-brand-700 px-2 py-1 rounded" onClick={seleccionarTodosMuni} data-testid="btn-muni-toda-prov">
              Marcar "toda la provincia"
            </button>
            <button type="button" className="text-xs text-ink-500 hover:text-ink-700 px-2 py-1 rounded" onClick={quitarTodosMuni}>
              Deseleccionar
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-1">
            {muniLoading && <p className="text-center text-ink-400 py-6 text-sm">Cargando municipios…</p>}
            {!muniLoading && muniList.length === 0 && (
              <p className="text-center text-ink-400 py-6 text-sm">No se encontraron municipios para esta provincia en la base de datos.</p>
            )}
            {!muniLoading && muniList.length > 0 && (
              (() => {
                const qq = muniSearch.trim().toLowerCase();
                const filtrados = qq ? muniList.filter((m) => m.nombre.toLowerCase().includes(qq) || m.codigo_ine?.startsWith(qq)) : muniList;
                const seleccionadosSet = new Set(municipiosSeleccionados.filter((x) => x !== '__NONE__'));
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-0.5">
                    {filtrados.map((m) => {
                      const checked = seleccionadosSet.has(m.codigo_ine);
                      return (
                        <label key={m.codigo_ine} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${checked ? 'bg-brand-50 text-brand-900' : 'hover:bg-ink-50 text-ink-700'}`} data-testid={`muni-row-${m.codigo_ine}`}>
                          <input
                            type="checkbox"
                            className="accent-brand-500"
                            checked={checked}
                            onChange={() => toggleMuni(m.codigo_ine)}
                          />
                          <span className="truncate">{m.nombre}</span>
                          <span className="text-[10px] text-ink-400 ml-auto">{m.codigo_ine}</span>
                        </label>
                      );
                    })}
                    {filtrados.length === 0 && <p className="col-span-full text-center text-sm text-ink-400 py-4">Sin resultados</p>}
                  </div>
                );
              })()
            )}
          </div>

          <div className="border-t border-ink-200 pt-2 px-1">
            <p className="text-xs text-ink-600">
              Estado actual: {municipiosSeleccionados.length === 0 || municipiosSeleccionados[0] === '__NONE__' ? (
                <span className="font-medium text-emerald-700">Toda la provincia</span>
              ) : (
                <span className="font-medium text-amber-700">{municipiosSeleccionados.filter((x) => x !== '__NONE__').length} municipios seleccionados (compartida)</span>
              )}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMuniDialog(null)} data-testid="btn-muni-done">Hecho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Zonas;
